import React, { useEffect } from 'react';
import { Star } from 'lucide-react';

// Google "Preferred Sources" button.
// SPA-safe MANUAL integration (the auto-rendered <div google-add-preferred-source-btn>
// breaks across React soft-navigations). Loader script lives in public/index.html
// with preferred-sources-control="manual". We render our own branded button and
// invoke the SDK on click, with a deeplink fallback until the SDK is ready / the
// domain is eligible. Docs: https://developers.google.com/search/docs/appearance/preferred-sources

const DOMAIN = 'buildpilot.com.au';

let _initialized = false;
let _ps = null;

function queueInit() {
  if (typeof window === 'undefined' || _initialized) return;
  _initialized = true;
  window.PREFERRED_SOURCE = window.PREFERRED_SOURCE || [];
  window.PREFERRED_SOURCE.push(function (preferredSource) {
    _ps = preferredSource;
    try {
      preferredSource.init({ theme: 'dark', lang: 'en' });
    } catch (e) {
      /* no-op */
    }
  });
}

// Variant styles:
//  pill    - dark pill, matches the footer social buttons (colours + h-9)
//  icon    - dark circle w-9 h-9, identical to a social button (star only)
//  menu    - full-width rounded row for the mobile hamburger (white on dark)
//  content - light pill for article/guide pages (white bg, brand-blue star)
const VARIANTS = {
  pill:
    'h-9 inline-flex items-center gap-2 px-4 rounded-full text-[13px] font-medium text-white bg-gray-800 hover:bg-[#1d8efe] transition-all duration-300',
  icon:
    'w-9 h-9 rounded-full flex items-center justify-center text-white bg-gray-800 hover:bg-[#1d8efe] transition-all duration-300',
  menu:
    'w-full flex items-center justify-center gap-2 py-3 rounded-full text-[14px] font-medium text-white border border-white/30 hover:bg-white/10 transition-colors',
  content:
    'inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-medium text-[#1d1d1f] bg-white border border-gray-200 hover:border-[#1d8efe] hover:text-[#1d8efe] transition-colors duration-300',
  badge:
    'w-full h-[40px] inline-flex items-center justify-center gap-2 px-3 rounded-[8px] text-white bg-black border border-gray-600 hover:border-[#1d8efe] transition-colors duration-300',
};

const LABELS = {
  pill: 'Preferred source',
  icon: null,
  menu: 'Add as a Google preferred source',
  content: 'Add BuildPilot as a preferred source',
};

export const PreferredSourceButton = ({ variant = 'pill', className = '', label, ...rest }) => {
  useEffect(() => {
    queueInit();
  }, []);

  const handleClick = () => {
    if (_ps && typeof _ps.addPreferredSource === 'function') {
      _ps.addPreferredSource();
    } else {
      window.open(
        `https://www.google.com/preferences/source?q=${DOMAIN}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  if (variant === 'badge') {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-label="Add BuildPilot as a preferred source on Google"
        title="Add BuildPilot as a preferred source on Google"
        className={`flex items-center justify-center ${className}`}
        {...rest}
      >
        <img
          src="/images/app/google-preferred-source-badge.png"
          alt="Add BuildPilot as a preferred source on Google"
          className="h-[34px] sm:h-[40px] w-auto"
          loading="lazy"
        />
      </button>
    );
  }

  const text = label !== undefined ? label : LABELS[variant];
  const starColor = variant === 'content' ? 'text-[#1d8efe]' : '';

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="preferred-source-btn"
      aria-label="Add BuildPilot as a preferred source on Google"
      title="Add BuildPilot as a preferred source on Google"
      className={`${VARIANTS[variant] || VARIANTS.pill} ${className}`}
      {...rest}
    >
      <Star size={variant === 'menu' ? 15 : 14} className={starColor} aria-hidden="true" />
      {text && <span>{text}</span>}
    </button>
  );
};

export default PreferredSourceButton;
